import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket, faCalendarDay, faArrowsRotate } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimePick from "components/timePick/TimePick";
import SelectNew from "components/select/SelectNew";
import {
  EMAIL_CAMPAIGN_RECURRING_OPTIONS,
  emailCampaignScheduleDate,
  emailCampaignScheduleTime,
  TEmailCampaignRecurringType,
  TEmailCampaignScheduleFields,
  TEmailCampaignScheduleType,
} from "./emailCampaignScheduleState";

type TEmailCampaignScheduleOptionsProps = {
  value: TEmailCampaignScheduleFields;
  onSelectType: (type: TEmailCampaignScheduleType) => void;
  onScheduledDateChange: (date: Date) => void;
  onScheduledTimeChange: (time: string | undefined) => void;
  onRecurringTypeChange: (type: TEmailCampaignRecurringType) => void;
};

type TScheduleOptionCardProps = {
  selected: boolean;
  expanded: boolean;
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
  description: string;
  onSelect: () => void;
  children?: React.ReactNode;
};

function ScheduleOptionCard(props: TScheduleOptionCardProps) {
  return (
    <div
      className={classNames("w-full rounded-xl", {
        "border-gray-700 border-2": props.selected,
        "border-gray-300 border": !props.selected,
      })}
    >
      <button
        type="button"
        className={classNames(
          "flex w-full cursor-pointer items-start gap-4 border-0 bg-transparent px-6 py-4 text-left",
          props.expanded && "pb-0 border-b",
        )}
        onClick={props.onSelect}
      >
        <div
          className={classNames(
            "mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-[28px] border-[6px] border-solid [&_svg]:size-5",
            props.iconClassName,
          )}
        >
          {props.icon}
        </div>
        <div
          className={classNames("flex min-w-0 flex-1 items-center justify-between gap-4", {
            'border-b border-gray-200 pb-4': props.expanded,
          })}
        >
          <div className="flex min-w-0 flex-col gap-1">
            <div className="text-sm font-medium leading-5 text-gray-700">{props.title}</div>
            <div className="text-sm font-normal leading-5 text-gray-600">{props.description}</div>
          </div>
          <div className="shrink-0 [&_.ui-checkbox_container]:mr-0 [&_.ui-choice]:m-0">
            <Checkbox circle size="medium" checked={props.selected} readOnly labelHidden />
          </div>
        </div>
      </button>

      {props.expanded && props.children ? (
        <div className="flex flex-col gap-4 overflow-visible px-4 pb-4">{props.children}</div>
      ) : null}
    </div>
  );
}

export function EmailCampaignScheduleOptions(props: TEmailCampaignScheduleOptionsProps) {
  const { t } = useTranslation();
  const { value } = props;
  const scheduledDate = emailCampaignScheduleDate(value);
  const scheduledTime = emailCampaignScheduleTime(value);
  const startingDate =
    scheduledDate.length > 0
      ? new Date(
          new Date(scheduledDate).getTime() + new Date().getTimezoneOffset() * 60 * 1000,
        )
      : undefined;

  return (
    <div className="space-y-4">
      <ScheduleOptionCard
        selected={value.scheduled_type === "now"}
        expanded={false}
        icon={<FontAwesomeIcon icon={faRocket} className="text-[#026aa2]" />}
        iconClassName="border-[#f0f9ff] bg-[#e0f2fe]"
        title={t("Send now")}
        description={t("Launch this campaign immediately.")}
        onSelect={() => props.onSelectType("now")}
      />

      <ScheduleOptionCard
        selected={value.scheduled_type === "later"}
        expanded={value.scheduled_type === "later"}
        icon={<FontAwesomeIcon icon={faCalendarDay} className="text-[#6927da]" />}
        iconClassName="border-[#f5f3ff] bg-[#ece9fe]"
        title={t("Schedule later")}
        description={t("Pick a specific date and time for delivery.")}
        onSelect={() => props.onSelectType("later")}
      >
        <p className="m-0 text-sm font-normal leading-5 text-gray-700">
          {t("Select a date and time to send your email campaign.")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePickerInput
            label={t("Choose date")}
            months={1}
            position="center"
            verticalPosition="top"
            startingDate={startingDate}
            setStartingDate={date => {
              if (date instanceof Date && !Number.isNaN(date.getTime())) {
                props.onScheduledDateChange(date);
              }
            }}
            disablePastDays
            allowEmptySingleDate
          />
          <TimePick
            label={t("Choose time")}
            value={scheduledTime || undefined}
            onChange={props.onScheduledTimeChange}
            align="right"
            positionTop
            size="large"
          />
        </div>
      </ScheduleOptionCard>

      <ScheduleOptionCard
        selected={value.scheduled_type === "recurring"}
        expanded={value.scheduled_type === "recurring"}
        icon={<FontAwesomeIcon icon={faArrowsRotate} className="text-[#a15c07]" />}
        iconClassName="border-[#fefbe8] bg-[#fef7c3]"
        title={t("Recurring")}
        description={t("Automate this email to repeat (e.g., Birthday emails, Monthly Newsletter).")}
        onSelect={() => props.onSelectType("recurring")}
      >
        <p className="m-0 text-sm font-normal leading-5 text-gray-700">
          {t("Select a date and time to send your email campaign.")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <DatePickerInput
            label={t("Start date")}
            months={1}
            position="left"
            verticalPosition="top"
            startingDate={startingDate}
            setStartingDate={date => {
              if (date instanceof Date && !Number.isNaN(date.getTime())) {
                props.onScheduledDateChange(date);
              }
            }}
            disablePastDays
            allowEmptySingleDate
          />
          <TimePick
            label={t("Start time")}
            value={scheduledTime || undefined}
            onChange={props.onScheduledTimeChange}
            align="right"
            positionTop
            size="large"
          />
          <SelectNew
            id="email-campaign-recurring-type"
            label={t("Send every")}
            placeholder={t("Select occurrence")}
            value={value.scheduled_recurring_type ?? ""}
            onChange={next => {
              props.onRecurringTypeChange(next as TEmailCampaignRecurringType);
            }}
            options={EMAIL_CAMPAIGN_RECURRING_OPTIONS.map(option => ({
              id: option.id,
              label: t(option.label),
            }))}
            dropdownPosition="top"
          />
        </div>
      </ScheduleOptionCard>
    </div>
  );
}
